import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { businessesAPI } from '../services/api';
import { hibritService } from '../../services/hibritService';
// 🛡️ ARCHITECTURE: useAuthBusiness kaldırıldı. Döngüsel bağımlılığı kırmak için veriler Prop olarak alınacak.

// Business Context oluştur
const BusinessContext = createContext();

// Business Context Hook
export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

// Business Provider Component
export const BusinessProvider = ({ children, saasBusinesses = [], saasLoading = false, refreshAuth }) => {
  const [selectedBusinessId, setSelectedBusinessId] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // 🎯 SaaS SYNC: Artık proplardan geliyor
  const authLoading = saasLoading;
  const loadBusinesses = refreshAuth;
  const [businesses, setBusinesses] = useState([]);

  // İşletmeleri senkronize et
  useEffect(() => {
    if (saasBusinesses && saasBusinesses.length > 0) {
      // Mevcut businesses ile aynı mı kontrol et (Sonsuz döngüyü kır)
      const currentSaasLength = businesses.filter(b => b.id !== 'all').length;
      if (currentSaasLength === saasBusinesses.length) {
        // İsim veya ID'lerde fark var mı diye hızlıca bak
        const isIdentical = saasBusinesses.every((sb, index) => {
          const cb = businesses.filter(b => b.id !== 'all')[index];
          return cb && cb.id === sb.id;
        });
        if (isIdentical) return; // Zaten aynı veriler var, güncelleme!
      }

      // "Tüm İşletmeler" opsiyonu ile birleştir
      setBusinesses([
        { id: 'all', name: 'Tüm İşletmeler', icon: '🏢', is_active: true },
        ...saasBusinesses
      ]);
    } else if (!authLoading && businesses.length !== 1) {
      setBusinesses([
        { id: 'all', name: 'Tüm İşletmeler', icon: '🏢', is_active: true }
      ]);
    }
  }, [saasBusinesses, authLoading]);

  // Selected business bilgisi
  const selectedBusiness = businesses.find(business => business.id === selectedBusinessId) ||
    businesses.find(business => business.id === 'all') ||
    businesses[0];

  // Get current user
  const getCurrentUser = () => {
    try {
      // Get actual user from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // console.log('👤 Current user from localStorage:', user);

      // Try different user ID fields for compatibility
      let userId = user.id || user.email || user.uid || user._id;

      // Don't create fallback user for unauthenticated users
      if (!userId) {
        console.warn('⚠️ No user ID found in BusinessContext');
        return null;
      }

      // console.log('🔑 Using user ID:', userId);
      return userId;
    } catch (error) {
      console.error('❌ Error getting current user:', error);
      return null; // No fallback user
    }
  };

  // Get icon for business sector
  const getBusinessIcon = (sector) => {
    const icons = {
      restaurant: '🍕',
      retail: '👕',
      service: '⚡',
      technology: '💻',
      health: '🏥',
      education: '🎓',
      finance: '💰',
      real_estate: '🏠',
      automotive: '🚗',
      default: '🏢'
    };
    return icons[sector] || icons.default;
  };

  // Load businesses from unified API with hibrit support
  // loadBusinesses lojiği AuthBusinessContext'e taşındı.
  // Bu context artık sadece bir veri sağlayıcısı/proxy görevi görüyor.

  // Business seçimini değiştir
  const selectBusiness = useCallback((businessId) => {
    setSelectedBusinessId(businessId);

    // LocalStorage'a kaydet
    localStorage.setItem('selectedBusinessId', businessId);

    // Seçili business objesini de kaydet
    const selectedBusiness = businesses.find(business => business.id === businessId);
    if (selectedBusiness) {
      localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
    }

    console.log(`✅ Business selected: ${businessId}`, selectedBusiness);
  }, [businesses]);

  // 🔄 SYNC WITH OTHER CONTEXTS
  useEffect(() => {
    const handleBusinessChanged = () => {
      const savedBusinessId = localStorage.getItem('selectedBusinessId');
      const savedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');

      // console.log('🔄 BusinessContext - Syncing with businessChanged event:', savedBusinessId);

      if (savedBusinessId && savedBusinessId !== selectedBusinessId) {
        setSelectedBusinessId(savedBusinessId);
      } else if (savedBusiness && savedBusiness.id !== selectedBusinessId) {
        setSelectedBusinessId(savedBusiness.id);
        localStorage.setItem('selectedBusinessId', savedBusiness.id);
      }
    };

    window.addEventListener('businessChanged', handleBusinessChanged);
    window.addEventListener('localStorageChange', handleBusinessChanged);

    return () => {
      window.removeEventListener('businessChanged', handleBusinessChanged);
      window.removeEventListener('localStorageChange', handleBusinessChanged);
    };
  }, [selectedBusinessId]);

  // İlk yüklemede localStorage'dan seçili business'i al
  useEffect(() => {
    const savedBusinessId = localStorage.getItem('selectedBusinessId');
    if (savedBusinessId && businesses.length > 0) {
      const businessExists = businesses.some(business => business.id === savedBusinessId);
      if (businessExists) {
        setSelectedBusinessId(savedBusinessId);
      }
    }
  }, [businesses]);

  // Business ekleme fonksiyonu
  const addBusiness = async (businessData) => {
    try {
      const userId = getCurrentUser();
      if (!userId) {
        throw new Error('User not found');
      }

      const response = await businessesAPI.create({
        ...businessData,
        user_id: userId
      });

      if (response.data && response.data.success) {
        // Reload businesses to include the new one
        await loadBusinesses();
        return response.data.business;
      } else {
        throw new Error(response.data?.message || 'Failed to create business');
      }
    } catch (error) {
      console.error('❌ Error adding business:', error);
      throw error;
    }
  };

  // Business güncelleme fonksiyonu
  const updateBusiness = async (businessId, businessData) => {
    try {
      const response = await businessesAPI.update(businessId, businessData);

      if (response.data && response.data.success) {
        // Reload businesses to reflect changes
        await loadBusinesses();
        return response.data.business;
      } else {
        throw new Error(response.data?.message || 'Failed to update business');
      }
    } catch (error) {
      console.error('❌ Error updating business:', error);
      throw error;
    }
  };

  // Business silme fonksiyonu
  const deleteBusiness = async (businessId) => {
    try {
      const response = await businessesAPI.delete(businessId);

      if (response.data && response.data.success) {
        // If the deleted business was selected, reset to "all"
        if (selectedBusinessId === businessId) {
          setSelectedBusinessId('all');
          localStorage.setItem('selectedBusinessId', 'all');
          localStorage.removeItem('selectedBusiness');
        }

        // Reload businesses to reflect changes
        await loadBusinesses();
        return true;
      } else {
        throw new Error(response.data?.message || 'Failed to delete business');
      }
    } catch (error) {
      console.error('❌ Error deleting business:', error);
      throw error;
    }
  };

  const contextValue = React.useMemo(() => ({
    // State
    businesses,
    selectedBusinessId,
    selectedBusiness,
    loading,
    error,

    // Actions
    selectBusiness,
    loadBusinesses,
    addBusiness,
    updateBusiness,
    deleteBusiness,

    // Utilities
    getBusinessIcon,
    getCurrentUser,

    // Backward compatibility aliases
    brands: businesses,
    selectedBrand: selectedBusiness,
    selectedBrandId: selectedBusinessId,
    selectBrand: selectBusiness,
    loadBrands: loadBusinesses,
  }), [
    businesses,
    selectedBusinessId,
    selectedBusiness,
    loading,
    error,
    selectBusiness,
    loadBusinesses,
    addBusiness,
    updateBusiness,
    deleteBusiness
  ]);

  return (
    <BusinessContext.Provider value={contextValue}>
      {children}
    </BusinessContext.Provider>
  );
};

export default BusinessContext;